import React, { Component } from 'react';
import dlv from 'dlv';
import dset from 'dset';
import format from 'date-fns/format';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import { Grid, Button } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';

import firebase, { db } from '../../helpers/firebase';
import BarChart from './plotting/BarChart';
import GraphCard from './plotting/GraphCard';
import DonutChart from './plotting/DonutChart';
import LinePlot from './plotting/LinePlot';
import styles from './Home.module.css';
import EditHoursButton from './extras/EditHoursButton';

export default class Kitchen extends Component {
  days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  constructor(props) {
    super(props);
    this.today = new Date();
    this.yearBack = new Date();
    this.yearBack.setDate(this.today.getDate() - 365);
    this.state = {
      dateRange: [this.yearBack, this.today],
      dataExists: false,
      mealAggregate: {},
      timelineItems: [],
      timelineData: {},
      timelineActiveData: {},
      allActive: false,
      wasteReasonData: {},
      weeklyData: {},
      mealHours: {
        // default meal hours if location has none
        breakfast: '07:30',
        lunch: '11:00',
        dinner: '16:30',
      },
    };
  }

  stringEqual = (a, b) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  async componentDidMount() {
    console.log('componentDidMount()...');
    if (this.props.diningHall && this.props.campus) {
      await this.getMealHours();
      this.updateDashboard();
    }
  }

  componentWillUnmount() {
    console.log('componentWillUnmount()...');
    // TO DO: save state for the next mount
    // const oldState = this.state;
  }

  shouldComponentUpdate(nextProps, nextState) {
    // TO DO: render only if props (data) actually changed
    return true;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const diffDateRange = !this.stringEqual(
      prevState.dateRange,
      this.state.dateRange
    );
    if (
      this.props.diningHall !== prevProps.diningHall ||
      diffDateRange ||
      this.props.diningHallPretty !== prevProps.diningHallPretty
    ) {
      this.updateDashboard();
    }
  }

  getMealHours = async () => {
    // console.log('getMealHours()...');
    const hoursSnapshot = await db
      .collection(`kitchen/testLocation/hours`) // TO DO: dynamic location name
      .doc('mealHours')
      .get();
    const hoursExist = !hoursSnapshot.empty;
    if (hoursExist) {
      this.setState({ mealHours: hoursSnapshot.data() });
      console.log('RENDER: getMealHours()');
    } else {
      console.log('Location has no meal hours');
    }
  };

  getMealPeriod = (timestamp) => {
    let period = '';
    var hrs = +timestamp.slice(-8, -6); // eg "2020-08-15 15:00:00"
    var min = +timestamp.slice(-5, -3);

    if (hrs < +this.state.mealHours.lunch.slice(0, 2)) {
      period = 'breakfast';
    } else if (hrs === +this.state.mealHours.lunch.slice(0, 2)) {
      if (min >= +this.state.mealHours.lunch.slice(3, 5)) {
        period = 'lunch';
      } else {
        period = 'breakfast';
      }
    } else if (hrs < +this.state.mealHours.dinner.slice(0, 2)) {
      period = 'lunch';
    } else if (hrs === +this.state.mealHours.dinner.slice(0, 2)) {
      if (min >= +this.state.mealHours.dinner.slice(3, 5)) {
        period = 'dinner';
      } else {
        period = 'lunch';
      }
    } else {
      period = 'dinner';
    }
    return period;
  };
  //   componentWillUnmount() {
  //     // Remember state for the next mount
  //     // Not using it right now...
  //     console.log('componentWillUnmount()...');
  //     // const oldState = this.state;
  //   }

  updateDashboard = async () => {
    // console.log('updateDashboard()...');
    const isoDateRange = [
      format(this.state.dateRange[0], 'yyyy-MM-dd'),
      format(this.state.dateRange[1], 'yyyy-MM-dd'),
    ];
    const dataInRange = await db
      .collection(`kitchen/testLocation/dates`) // TO DO: dynamic location name
      .where(firebase.firestore.FieldPath.documentId(), '>=', isoDateRange[0])
      .where(firebase.firestore.FieldPath.documentId(), '<=', isoDateRange[1])
      .get();

    const dataExists = !dataInRange.empty;
    if (dataExists) {
      let mealAggregate = {};
      let mealTotals = { breakfast: 0, lunch: 0, dinner: 0 };
      let timelineData = {};
      let timelineItems = [];
      let wasteReasonData = {};
      let weeklyData = {};

      dataInRange.docs.forEach((dateDoc) => {
        // for every date (2020-10-12, ...)
        const date = dateDoc.id;
        Object.entries(dateDoc.data()).forEach(([item, timestamps]) => {
          // for every item (bread, fruit, ...)

          // Timeline
          let itemDatapoint = { x: `${date}`, y: 0 };
          if (!timelineItems.includes(item)) {
            timelineItems.push(item);
            dset(timelineData, `${item}.id`, item);
            dset(timelineData, `${item}.data`, []);
          }

          Object.entries(timestamps).forEach(([timestamp, entry]) => {
            // for every data point (['2020-10-12 10:00:00': {value:123, reason:spoil}, ...])

            // Donut charts
            const meal = this.getMealPeriod(timestamp);
            if (entry.value > 0.5) {
              const currentValue = dlv(mealAggregate, `${meal}.${item}`, 0);
              const currentMealTotal = dlv(mealTotals, `${meal}`, 0);
              dset(
                mealAggregate,
                `${meal}.${item}`,
                currentValue + entry.value
              );
              dset(mealTotals, `${meal}`, currentMealTotal + entry.value);
            }

            // Timeline
            const currentY = dlv(itemDatapoint, 'y', 0);
            dset(itemDatapoint, 'y', currentY + entry.value);

            // Waste Reason
            const reason = entry.reason;
            if (!Object.keys(wasteReasonData).includes(reason)) {
              dset(wasteReasonData, `${reason}`, {
                reason: `${reason}`,
                [reason]: entry.value,
              });
            } else {
              const currentR = dlv(wasteReasonData, `${reason}.${reason}`, 0);
              dset(
                wasteReasonData,
                `${reason}.${reason}`,
                currentR + entry.value
              );
            }

            // Weekly
            const dayOfWeek = this.days[new Date(timestamp).getDay()];
            if (!Object.keys(weeklyData).includes(dayOfWeek)) {
              dset(weeklyData, `${dayOfWeek}`, {
                day: dayOfWeek,
                [item]: entry.value,
              });
            } else {
              const currentW = dlv(weeklyData, `${dayOfWeek}.${item}`, 0);
              dset(weeklyData, `${dayOfWeek}.${item}`, currentW + entry.value);
            }
          });
          // Timeline
          timelineData[item].data.push(itemDatapoint);
        });
      });

      // Normalize donut charts
      Object.entries(mealAggregate).forEach(([meal, items]) => {
        Object.entries(items).forEach(([item, value]) => {
          const mealTotal = dlv(mealTotals, `${meal}`);
          const normValue = +((value * 100) / mealTotal).toFixed(2);
          if (normValue >= 1) {
            // group all slices <1% into Other
            dset(mealAggregate, `${meal}.${item}`, normValue);
          } else if (normValue === 0) {
            delete mealAggregate[meal][item];
          } else {
            delete mealAggregate[meal][item];
            const otherValue = dlv(mealAggregate, `${meal}.other`, 0);
            dset(mealAggregate, `${meal}.other`, otherValue + normValue);
          }
        });
      });

      // Determine default active series
      let timelineActiveData = {};
      if (timelineItems.length > 2) {
        for (let i = 0; i < 2; i++) {
          const itemName = timelineItems[i];
          timelineActiveData = {
            ...timelineActiveData,
            [itemName]: timelineData[itemName],
          };
        }
      } else {
        const firstItem = timelineItems[0];
        timelineActiveData = { firstItem: timelineData[firstItem] };
      }

      this.setState({
        dataExists,
        mealAggregate,
        timelineData,
        timelineItems,
        timelineActiveData,
        wasteReasonData,
        weeklyData,
      });
      console.log('RENDER: updateDashboard()');
    } else {
      this.setState({ dataExists });
      console.log('No data for selected date range');
    }
  };

  timelineItemHandler = (activeItems, event) => {
    // activeItems=[{value:'', id:1}, ...]
    if (activeItems[0].id === '*' && event.option.id !== '*') {
      let newActiveData = { ...this.state.timelineActiveData };
      delete newActiveData[event.option.label];
      this.setState({
        timelineActiveData: newActiveData,
        allActive: false,
      });
    } else if (event.option.id === '*') {
      if (event.action === 'select-option') {
        // select all
        this.setState({
          timelineActiveData: this.state.timelineData,
          allActive: true,
        });
      } else {
        // deselect all
        this.setState({
          timelineActiveData: {},
          allActive: false,
        });
      }
    } else {
      let timelineActiveData = {};
      activeItems.forEach((item) => {
        const itemName = item.label;
        timelineActiveData = {
          ...timelineActiveData,
          [itemName]: this.state.timelineData[itemName],
        };
      });
      this.setState({
        timelineActiveData,
      });
    }
    console.log('RENDER: timelineItemHandler()');
  };

  validateMealHours = (event, form) => {
    // TO DO: validate that breakfast<lunch<dinner
    event.preventDefault(); // no refresh on form submit
    let hoursChanged = false;
    let newHours = {};
    for (let input of form) {
      if (input.defaultValue !== input.value) {
        let meal = input.name;
        newHours = { ...newHours, [meal]: input.value };
        if (!hoursChanged) {
          console.log('Hours changed');
          hoursChanged = true;
        }
      }
    }
    if (hoursChanged) {
      let currentHours = { ...this.state.mealHours };
      Object.assign(currentHours, newHours);
      this.saveMealHours(currentHours);
    } else {
      console.log("Hours didn't change");
    }
  };

  saveMealHours = async (hours) => {
    await db
      .collection(`kitchen/testLocation/hours`) // TO DO: dynamic location name
      .doc('mealHours')
      .set(hours);
    this.setState({ mealHours: hours });
    console.log('RENDER: saveMealHours()');
  };

  onDateRangeChange = (date) => {
    this.setState({ dateRange: date });
    console.log('Date has been changed!!');
  };

  dateLastWeek = () => {
    let today = new Date();
    let lastWeek = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - 7
    );
    this.setState({ dateRange: [lastWeek, today] });
  };

  dateLastMonth = () => {
    let today = new Date();
    let lastMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - 30
    );
    this.setState({ dateRange: [lastMonth, today] });
  };

  dateLastYear = () => {
    let today = new Date();
    let lastYear = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - 365
    );
    this.setState({ dateRange: [lastYear, today] });
  };

  render() {
    // console.log('[Kitchen.js] rendering...');
    return (
      <div>
        <div className={styles.datePickerContainer}>
          <DateRangePicker
            onChange={this.onDateRangeChange}
            value={this.state.dateRange}
          />
          <Button
            className={styles.dateRangeButton}
            variant="contained"
            onClick={this.dateLastWeek}
          >
            Last Week
          </Button>
          <Button
            className={styles.dateRangeButton}
            variant="contained"
            onClick={this.dateLastMonth}
          >
            Last Month
          </Button>
          <Button
            className={styles.dateRangeButton}
            variant="contained"
            onClick={this.dateLastYear}
          >
            Last Year
          </Button>
        </div>
        <div
          className={styles.dateRangeButtonsContainer}
          data-html2canvas-ignore="true"
        >
          <EditHoursButton
            breakfast={this.state.mealHours.breakfast}
            lunch={this.state.mealHours.lunch}
            dinner={this.state.mealHours.dinner}
            validator={this.validateMealHours}
          />
          <Button
            className={styles.dateRangeButton}
            variant="contained"
            onClick={this.createReport}
            startIcon={<GetAppIcon />}
          >
            Download Report
          </Button>
        </div>

        {this.state.dataExists ? (
          <Grid container spacing={0} justify="center" alignItems="flex-start">
            <Grid item xs={4} sm={4} md={4}>
              <GraphCard
                title={'Breakfast'}
                plot={<DonutChart data={this.state.mealAggregate.breakfast} />}
              />
            </Grid>
            <Grid item xs={4} sm={4} md={4}>
              <GraphCard
                title={'Lunch'}
                plot={<DonutChart data={this.state.mealAggregate.lunch} />}
              />
            </Grid>
            <Grid item xs={4} sm={4} md={4}>
              <GraphCard
                title={'Dinner'}
                plot={<DonutChart data={this.state.mealAggregate.dinner} />}
              />
            </Grid>
            <Grid item xs={7} sm={7} md={7}>
              <GraphCard
                title={'Waste Over Time'}
                plot={<LinePlot data={this.state.timelineActiveData} />}
                dispOptions={true}
                activeOps={this.state.timelineActiveData}
                allActive={this.state.allActive}
                options={this.state.timelineItems}
                changer={this.timelineItemHandler}
              />
            </Grid>
            <Grid item xs={5} sm={5} md={5}>
              <GraphCard
                title={'Waste Reasons'}
                plot={
                  <BarChart
                    data={Object.values(this.state.wasteReasonData)}
                    keys={Object.keys(this.state.wasteReasonData)}
                    index={'reason'}
                    showX={false}
                  />
                }
              />
            </Grid>
            {console.log(this.state.weeklyData)}
            <Grid item xs={12} sm={10} md={10}>
              <GraphCard
                title={'Weekly Report'}
                plot={
                  <BarChart
                    data={Object.values(this.state.weeklyData)}
                    keys={this.state.timelineItems}
                    index={'day'}
                    showX={true}
                  />
                }
              />
            </Grid>
          </Grid>
        ) : (
          <div className={styles.empty}>No data for selected date range...</div>
        )}
      </div>
    );
  }
}
